import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { ListingsAdmin } from "@/components/admin/ListingsAdmin";
import { RequestsAdmin } from "@/components/admin/RequestsAdmin";

const Stat = ({ n, l, testid }) => (
  <div className="bg-white border p-5">
    <p data-testid={testid} className="font-cond font-bold text-4xl">{n}</p>
    <p className="text-xs text-[var(--mute-l)] mt-1">{l}</p>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const [listings, setListings] = useState([]);
  const [requests, setRequests] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([http.get("/admin/listings"), http.get("/admin/requests")]);
      setListings(Array.isArray(a.data) ? a.data : []);
      setRequests(Array.isArray(b.data) ? b.data : []);
    } catch { /* handled by auth guard */ }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--green)]" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const nuove = requests.filter((r) => r.stato === "nuova").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--paper)]">
      <header className="bg-[var(--ink)] text-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <img src="/img/logo_ivcasa_white_sm.png" alt="IVCasa" className="h-8" />
            <span className="hidden sm:block eyebrow text-[var(--brass)]">Area riservata</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/" className="hidden sm:flex items-center gap-1 text-[var(--mute-d)] hover:text-[var(--cream)]" data-testid="admin-view-site-link">Vedi sito <ExternalLink className="w-3.5 h-3.5" /></Link>
            <span className="hidden md:block text-[var(--mute-d)]">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 hover:text-[var(--green)]"><LogOut className="w-4 h-4" /> Esci</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl sm:text-5xl">Gestione IVCasa</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat n={listings.length} l="Annunci totali" testid="admin-stat-annunci" />
          <Stat n={listings.filter((l) => l.pubblicato).length} l="Pubblicati" testid="admin-stat-pubblicati" />
          <Stat n={listings.filter((l) => l.in_evidenza).length} l="In evidenza" testid="admin-stat-evidenza" />
          <Stat n={nuove} l="Nuove richieste" testid="admin-stat-richieste" />
        </div>
        <Tabs defaultValue="annunci" className="mt-10">
          <TabsList>
            <TabsTrigger value="annunci" data-testid="admin-tab-annunci">Annunci</TabsTrigger>
            <TabsTrigger value="richieste" data-testid="admin-tab-richieste">Richieste {nuove ? `(${nuove})` : ""}</TabsTrigger>
          </TabsList>
          <TabsContent value="annunci" className="mt-6"><ListingsAdmin items={listings} reload={load} /></TabsContent>
          <TabsContent value="richieste" className="mt-6"><RequestsAdmin items={requests} reload={load} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
